import Vue from 'vue';
import Vuex from 'vuex';
import firebase from 'firebase';
import { User } from '@/models/user';
import { Crop } from '@/models/crop';
import { CropType } from '@/models/crop-type';
import { Variety } from '@/models/variety';
import { GrowthCycleTemplate, Raft } from '@/models/growth-cycle-template';
import { GrowthCycle } from '@/models/growth-cycle';
import { ToDo, TaskName } from '@/models/todo';
import TodoService from '@/services/todo-service';
import { Farm } from '@/models/farm';
import router from '@/router';
import moment from 'moment';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    currentUser: {},
    currentFarm: {},
    crops: [
      {
        id: '',
        name: '',
        cropTypes: [
          {
            id: '',
            name: '',
            cropId: '',
            varieties: [],
          },
        ],
      },
    ],
    allVarieties: [],
    // Global notification messages.
    messages: {
      success: '',
      error: '',
      loading: false,
    },
    growthCycles: [],
    growthCycleTemplates: [],
    raftTypes: [],
    todos: [],
    farms: [],
  },
  mutations: {
    updateCurrentUser(state, payload) {
      state.currentUser  = { ...payload };
    },
    setMessage(state, payload) {
      state.messages = { ...payload };
    },
    syncCropsState(state, payload) {
      state.crops = payload.map((item: Crop) => {
        return {
          id: item.id,
          name: item.name,
        };
      });
    },
    syncCropTypes(state, payload) {
      const updatedCrops = state.crops.map((crop) => {
        if (crop.id === payload.cropId) {
          return Object.assign({}, {id: crop.id, name: crop.name, cropTypes: payload.cropTypes});
        }
        return crop;
      });

      state.crops = updatedCrops;
    },
    syncVarieties(state, payload) {
      const updatedCrops = state.crops.map((crop) => {
        if (crop.id === payload.cropId) {
          return Object.assign({}, {
            id: crop.id,
            name: crop.name,
            cropTypes: crop.cropTypes.map((cropType) => {
            if (cropType.id === payload.cropTypeId) {
              return Object.assign({}, {
                    id: cropType.id,
                    name: cropType.name,
                    cropId: cropType.cropId,
                    varieties: payload.varieties,
                  },
                );
              }
            return cropType;
            })},
          );
        }
        return crop;
      });

      state.crops = updatedCrops;
    },
    syncAllVarieties(state, payload) {
      state.allVarieties = payload.map((item: Variety) => {
        return { ...item };
      });
    },
    syncGrowthCycleTemplates(state, payload) {
      state.growthCycleTemplates = payload.map((item: GrowthCycleTemplate) => {
        return { ...item };
      });
    },
    syncRaftTypes(state, payload) {
      state.raftTypes = payload.map((item: Raft) => {
        return { ...item };
      });
    },
    syncGrowthCycles(state, payload) {
      state.growthCycles = payload.map((item: any) => {
        const timestampKeys =
          ['startedAt', 'germinationStartedAt',
          'propagationStartedAt', 'highDensityStartedAt',
          'finishedSpacingStartedAt', 'harvestAt', 'estimatedHarvestDate'];
        const parsedItem: any = {};
        for (const key in item) {
          if (timestampKeys.indexOf(key) > -1 && item[key]) {
            parsedItem[key] = moment(item[key].toDate());
          } else {
            parsedItem[key] = item[key];
          }
        }
        return { ...parsedItem };
      });
    },
    syncTodos(state, payload) {
      state.todos = payload.map((item: ToDo) => {
        item.dueDate = moment(item.dueDate.toDate());
        return { ...item };
      });
    },
    syncFarms(state, payload) {
      state.farms = payload;
    },
    setCurrentFarm(state, payload) {
      state.currentFarm = payload;
    },
    setLoading(state, payload) {
      state.messages.loading = payload;
    },
    setSuccessMessage(state, payload) {
      state.messages.success = payload;
    },
    setErrorMessage(state, payload) {
      state.messages.error = payload;
    },
  },
  actions: {
    getCurrentUser({commit}) {
      const dbRef = firebase.firestore();
      const currentUser: firebase.User | null = firebase.auth().currentUser;

      if (currentUser !== null) {
        const usersRef = dbRef.collection('users');
        usersRef
          .where('email', '==', currentUser.email)
          .limit(1)
          .get()
          .then(
            (snapshot) => {
              const userData = snapshot.docs[0].data();
              const userId = snapshot.docs[0].id;
              const user = new User(
                userData.email,
                userId,
                userData.firstName || '',
                userData.lastName || '',
              );
              commit('updateCurrentUser', user);
            },
          )
          .catch(
            (err) => {
              alert('User not found in database; please contact an administrator to be added to the application.');
              firebase.auth().signOut().then(() => router.replace('/login'));
            },
          );
      }
    },
    getCrops({commit}) {
      const cropsRef = firebase.firestore().collection('crops');
      commit('setLoading', true);

      cropsRef.orderBy('name').get().then(
        (snapshot) => {
          const cropsData = snapshot.docs;
          const crops: Crop[] = [];

          cropsData.map((value) => {
            crops.push({
              id: value.id,
              name: value.data().name,
            });
          });

          commit('syncCropsState', crops);
          commit('setLoading', false);
        },
      );
    },
    getCropTypes({commit}, cropId: string) {
      const cropsRef = firebase.firestore().collection('crops');
      const cropTypesRef = firebase.firestore().collection('cropTypes');
      commit('setLoading', true);

      const reference = cropsRef.doc(cropId);

      cropTypesRef.where('cropId', '==', reference).get().then(
        (snapshot) => {
          const cropTypeData = snapshot.docs;
          const cropTypes: CropType[] = [];

          cropTypeData.map((value) => {
            cropTypes.push({
              id: value.id,
              name: value.data().name,
              varieties: value.data().varieties ? value.data().varieties : [],
              cropId,
            });
          });

          commit('syncCropTypes', {cropId, cropTypes});
          commit('setLoading', false);
        },
      );
    },
    getVarieties({commit}, {cropId, cropTypeId}) {
      const cropTypesRef = firebase.firestore().collection('cropTypes');
      const varietiesRef = firebase.firestore().collection('varieties');
      commit('setLoading', true);

      const reference = cropTypesRef.doc(cropTypeId);

      varietiesRef.where('cropTypeId' , '==', reference).get().then(
        (snapshot) => {
          const varietyData = snapshot.docs;
          const varieties: Variety[] = [];

          varietyData.map((value) => {
            varieties.push({
              id: value.id,
              name: value.data().name,
              seedType: value.data().seedType,
              cropCode: value.data().cropCode,
              cropTypeId,
            });
          });

          commit('syncVarieties', {cropId, cropTypeId, varieties});
          commit('setLoading', false);
        },
      );
    },
    getAllVarieties({commit}) {
      const varietiesRef = firebase.firestore().collection('varieties');
      const varieties: Variety[] = [];

      varietiesRef.orderBy('name').get().then((snapshot) => {
        snapshot.forEach((doc) => {
          const varietyData: Variety = doc.data() as Variety;
          varieties.push({id: doc.id, ...varietyData});
        });
        commit('syncAllVarieties', varieties);
      });
    },
    getGrowthCycleTemplates({commit}) {
      const growthCycleTemplatesRef = firebase.firestore().collection('growthCycleTemplates');
      const templates: GrowthCycleTemplate[] = [];

      growthCycleTemplatesRef.get().then((snapShot) => {
          snapShot.forEach((doc) => {
            const templateData: GrowthCycleTemplate = doc.data() as GrowthCycleTemplate;
            templates.push({id: doc.id, ...templateData});
          });
          commit('syncGrowthCycleTemplates', templates);
      });
    },
    addGrowthCycleTemplate({commit}, payload: GrowthCycleTemplate) {
      const growthCycleTemplatesRef = firebase.firestore().collection('growthCycleTemplates');

      growthCycleTemplatesRef.add(payload)
        .then(() => this.dispatch('getGrowthCycleTemplates'));
    },
    getRaftTypes({commit}) {
      const raftTypesRef = firebase.firestore().collection('rafts');
      const raftTypes: Raft[] = [];

      raftTypesRef.get().then((snapshot) => {
        snapshot.forEach((doc) => {
          const raftData: Raft = doc.data() as Raft;
          raftTypes.push({...raftData});
        });
        commit('syncRaftTypes', raftTypes);
      });
    },
    getGrowthCycles({commit}) {
      const growthCyclesRef = firebase.firestore().collection('growthCycles');
      const growthCycles: GrowthCycle[] = [];

      growthCyclesRef.get().then((snapshot) => {
        snapshot.forEach((doc) => {
          const growthCycleData: GrowthCycle = doc.data() as GrowthCycle;
          growthCycles.push({...growthCycleData});
        });
        commit('syncGrowthCycles', growthCycles);
      });
    },
    addGrowthCycle({commit}, payload: GrowthCycle) {
      const growthCyclesRef = firebase.firestore().collection('growthCycles');
      const todoService = new TodoService();

      growthCyclesRef.add(payload)
        .then((doc) => {
          todoService.addFirstTodo(doc);
          this.dispatch('getGrowthCycles');
        });
    },
    getIncompleteTodos({commit}) {
      const todosRef = firebase.firestore().collection('todos');
      const todos: ToDo[] = [];

      todosRef.where('completed', '==', false).orderBy('dueDate').get().then((snapshot) => {
        snapshot.forEach((doc) => {
          const todoData: ToDo = doc.data() as ToDo;
          todoData.id = doc.id;
          todos.push({ ...todoData });
        });
        commit('syncTodos', todos);
      });
    },
    addTodo({commit}, payload: ToDo) {
      const todosRef = firebase.firestore().collection('todos');

      todosRef.add(payload);
    },
    getFarms({commit}) {
      const farmsRef = firebase.firestore().collection('farms');
      const farms: Farm[] = [];

      farmsRef.get().then((snapshot) => {
        snapshot.forEach((doc) => {
          const farmData: Farm = doc.data() as Farm;
          farmData.id = doc.id;
          farms.push(farmData);
        });
        commit('syncFarms', farms);
      });
    },
    setCurrentFarm({commit}, payload: Farm) {
      commit('setCurrentFarm', payload);
    },
    setLoading({commit}) {
      commit('setLoading', true);
    },
    unsetLoading({commit}) {
      commit('setLoading', false);
    },
    setError({commit}, payload: string) {
      commit('setErrorMessage', payload);
    },
  },
});
